import type { GameConfig } from '../../data/games';
import type { SessionMode } from '../../utils/trainingProtocol';
import type { ChallengeTier, TrialRecord } from './types';
import { clamp, summarizeTrials } from './rules';

export type RecallRule = 'forward' | 'reverse' | 'mirror';
export const RECALL_NAMES: Record<RecallRule, string> = { forward: '顺序重现', reverse: '倒序重现', mirror: '左右镜像' };

export function getMemoryRules(game: GameConfig, mode: SessionMode, tier: ChallengeTier) {
  const level = mode === 'standard' ? 0 : tier;
  return {
    level,
    rounds: 6 + level,
    grid: game.mechanic === 'sequence' ? 3 : level >= 2 ? 4 : 3,
    startSpan: 3 + (level >= 2 ? 1 : 0),
    maxSpan: 5 + level,
    flashMs: 720 - level * 100,
    gapMs: 250,
    retentionMs: 700 + level * 350,
    mistakeLimit: level === 0 ? 4 : level === 1 ? 3 : 2,
    replayBudget: level === 0 ? 2 : level === 1 ? 1 : 0,
    inputPerItemMs: 1900 - level * 220,
  };
}

export function recallRuleForRound(round: number, level: number, spatial: boolean): RecallRule {
  if (spatial && level === 3 && round % 3 === 2) return 'mirror';
  return level >= 2 && round % 2 === 1 ? 'reverse' : 'forward';
}

export function createRecallSequence(length: number, grid: number, random = Math.random): number[] {
  const result: number[] = [];
  for (let i = 0; i < length; i++) {
    // Never repeat adjacent cells; even a constant random source remains safe.
    const choices = Array.from({ length: grid * grid }, (_, n) => n).filter(n => n !== result[i - 1]);
    result.push(choices[Math.min(choices.length - 1, Math.floor(random() * choices.length))]);
  }
  return result;
}

export function recallAnswer(sequence: number[], rule: RecallRule, grid: number): number[] {
  if (rule === 'reverse') return [...sequence].reverse();
  if (rule === 'mirror') return sequence.map(n => Math.floor(n / grid) * grid + grid - 1 - n % grid);
  return [...sequence];
}

export function nextMemorySpan(span: number, correct: boolean, maxSpan: number): number {
  return clamp(span + (correct ? 1 : -1), 3, maxSpan);
}

export function summarizeRecall(trials: TrialRecord[], planned: number, replayUses: number) {
  const summary = summarizeTrials(trials, planned);
  return { ...summary, score: Math.max(0, summary.score - replayUses * 5) };
}

export function getNBackRules(mode: SessionMode, tier: ChallengeTier) {
  const level = mode === 'standard' ? 0 : tier;
  return {
    level,
    n: level === 0 ? 1 : level === 3 ? 3 : 2,
    rounds: 12 + level * 2,
    windowMs: 3600 - level * 450,
    gapMs: 550,
    mistakeLimit: level === 0 ? 6 : level === 1 ? 5 : 4,
  };
}

export interface NBackStimulus { cell: number; match: boolean | null; lure: boolean; }
export function createNBackStream(n: number, rounds: number, level: number, random = Math.random): NBackStimulus[] {
  // Exact balance prevents a single repeated answer from passing as a strong run.
  const flags = Array.from({ length: rounds }, (_, i) => i < Math.floor(rounds / 2));
  for (let i = flags.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [flags[i], flags[j]] = [flags[j], flags[i]];
  }
  const stream: NBackStimulus[] = [];
  for (let i = 0; i < rounds + n; i++) {
    const expected = i >= n ? stream[i - n].cell : -1;
    const match = i >= n ? flags[i - n] : null;
    const lureCell = i > 0 ? stream[i - 1].cell : -1;
    const lure = match === false && level >= 2 && n > 1 && lureCell !== expected && random() < 0.65;
    const candidates = Array.from({ length: 9 }, (_, c) => c).filter(c => c !== expected);
    const cell = match ? expected : lure ? lureCell : candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
    stream.push({ cell, match, lure });
  }
  return stream;
}

export function judgeNBack(match: boolean, answer: boolean | null) {
  if (answer === null) return { outcome: 'timeout' as const, label: match ? '漏判相同' : '未作答', kind: match ? 'miss' : 'timeout' };
  if (answer === match) return { outcome: 'perfect' as const, label: match ? '正确命中' : '正确排除', kind: match ? 'hit' : 'reject' };
  return { outcome: 'wrong' as const, label: answer ? '误报相同' : '漏判相同', kind: answer ? 'falseAlarm' : 'miss' };
}
